import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { haptic } from '../lib/telegram';
import { cn } from '../lib/utils';
import { Card } from './Card';

/**
 * Prompts users to join their nation's WC country league. Renders only when
 * the user is currently in zero COUNTRY-type leagues. The full list of
 * 48 country leagues is fetched on tap from /api/leagues/public.
 */
export function CountryJoinBanner() {
  const qc = useQueryClient();
  const myLeagues = useQuery({ queryKey: ['leagues', 'me'], queryFn: () => api.leaguesMe() });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const publicLeagues = useQuery({
    queryKey: ['leagues', 'public'],
    queryFn: () => api.leaguesPublic(),
    enabled: open,
  });

  const join = useMutation({
    mutationFn: (slug: string) => api.joinLeague(slug),
    onSuccess: async () => {
      haptic.success();
      await qc.invalidateQueries({ queryKey: ['leagues', 'me'] });
      setOpen(false);
      setSearch('');
    },
    onError: () => haptic.error(),
  });

  // Only show the prompt when the user has joined zero COUNTRY leagues yet.
  const hasCountryLeague = (myLeagues.data?.leagues ?? []).some(
    ({ league }) => league.leagueType === 'COUNTRY',
  );
  if (hasCountryLeague) return null;
  if (myLeagues.isLoading) return null;

  const countryList = (publicLeagues.data?.leagues ?? []).filter(
    (l) => l.leagueType === 'COUNTRY',
  );
  const filtered = search.trim().length > 0
    ? countryList.filter((l) => l.name.toLowerCase().includes(search.trim().toLowerCase()))
    : countryList;

  if (!open) {
    return (
      <Card className="border-pitch/40">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display-alt text-xs uppercase tracking-widest text-pitch">
              Country league
            </div>
            <div className="font-chunky text-sm mt-0.5 truncate">Find your nation's league</div>
            <div className="text-xs text-text-dim mt-0.5">
              One per WC 2026 qualifier — open to all fans.
            </div>
          </div>
          <button
            onClick={() => {
              haptic.tap();
              setOpen(true);
            }}
            className="shrink-0 rounded-md bg-pitch text-bg-night font-chunky text-xs uppercase tracking-widest px-3 py-2"
          >
            Pick
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-pitch/40 space-y-3">
      <div className="flex items-baseline justify-between">
        <div className="font-display-alt text-xs uppercase tracking-widest text-pitch">
          Pick your country
        </div>
        <button
          onClick={() => setOpen(false)}
          className="text-text-faint text-xs"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
      <input
        autoFocus
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search 48 nations..."
        className="w-full rounded-lg bg-bg-night border border-border-base px-3 py-2 text-text-base outline-none focus:border-pitch"
      />
      {publicLeagues.isLoading && (
        <div className="text-text-dim text-xs">Loading leagues...</div>
      )}
      <div className="max-h-64 overflow-y-auto space-y-1">
        {filtered.map((l) => (
          <button
            key={l.id}
            onClick={() => {
              haptic.select();
              join.mutate(l.slug);
            }}
            disabled={join.isPending}
            className={cn(
              'w-full text-left rounded-md px-3 py-2 transition-colors',
              'bg-bg-elevated text-text-base hover:bg-bg-card',
              join.isPending && 'opacity-50',
            )}
          >
            <div className="flex items-center justify-between">
              <span className="font-chunky text-sm">{l.name}</span>
              <span className="font-mono text-[10px] text-text-faint">
                {l.memberCount} {l.memberCount === 1 ? 'member' : 'members'}
              </span>
            </div>
          </button>
        ))}
        {filtered.length === 0 && !publicLeagues.isLoading && (
          <div className="text-text-faint text-xs text-center py-4">
            No match. Try another spelling.
          </div>
        )}
      </div>
    </Card>
  );
}
